// ============================================================
// Mitarbeiter-Verwaltung — Beschäftigte der Mandanten (Firmen).
// Namen und Geburtsdaten werden CLIENT-SEITIG verschlüsselt gespeichert
// (name_enc/geburtsdatum_enc, AES-256-GCM mit dem Session-Key). Auch bei
// der zentralen Datenbank liegen diese Felder nur als Chiffretext.
// Braucht eine aktive Session (entsperrt) zum Ent-/Verschlüsseln.
// ============================================================

import { db, type Mitarbeiter } from '../db/schema';
import { encryptString, decryptToString, uint8ToBase64, base64ToUint8 } from '../services/crypto';
import { SessionKeyStore } from '../services/keyStore';
import { logAktion } from '../services/auditLogService';
import {
  toMitarbeiterID, toArbeitsplatzID, toMandantID,
  type MitarbeiterID, type MandantID,
} from '../types/branded';

/** Entschlüsselte Ansicht für die UI (Klarnamen NUR im RAM). */
export interface MitarbeiterView {
  id:            string;
  mandant_id:    string;
  arbeitsplatz_id: string;
  vorname:       string;
  nachname:      string;
  geburtsdatum:  string;   // ISO-Date oder ''
  geschlecht:    'M' | 'W' | 'D' | 'U';
  aktiv:         boolean;
  erstellt_am:   string;
}

function ensureSession(): void {
  if (!SessionKeyStore.isActive()) {
    throw new Error('Sitzung gesperrt — bitte mit Master-Passwort entsperren.');
  }
}

async function encField(text: string): Promise<string> {
  return uint8ToBase64(await encryptString(text));
}

async function decField(b64: string): Promise<string> {
  try {
    return await decryptToString(base64ToUint8(b64));
  } catch {
    return '⚠ nicht entschlüsselbar';
  }
}

// ── Laden (entschlüsselt) ──────────────────────────────────────────────────

export async function loadMitarbeiter(mandantId?: string | null): Promise<MitarbeiterView[]> {
  ensureSession();
  const rows = mandantId !== undefined && mandantId !== null && mandantId !== ''
    ? await db.mitarbeiter.where('mandant_id').equals(mandantId).toArray()
    : await db.mitarbeiter.toArray();

  const views = await Promise.all(rows.map(async (m): Promise<MitarbeiterView> => {
    let vorname = '', nachname = '';
    try {
      const parsed = JSON.parse(await decField(m.name_enc)) as { vorname?: string; nachname?: string };
      vorname = parsed.vorname ?? '';
      nachname = parsed.nachname ?? '';
    } catch {
      nachname = await decField(m.name_enc);
    }
    return {
      id: m.id,
      mandant_id: m.mandant_id,
      arbeitsplatz_id: m.arbeitsplatz_id,
      vorname,
      nachname,
      geburtsdatum: m.geburtsdatum_enc !== '' ? await decField(m.geburtsdatum_enc) : '',
      geschlecht: m.geschlecht,
      aktiv: m.aktiv,
      erstellt_am: m.erstellt_am,
    };
  }));

  return views.sort((a, b) => (a.nachname + a.vorname).localeCompare(b.nachname + b.vorname));
}

// ── Speichern (verschlüsselt) ──────────────────────────────────────────────

export interface MitarbeiterInput {
  id?:           string;
  mandant_id:    string;
  arbeitsplatz_id: string;
  vorname:       string;
  nachname:      string;
  geburtsdatum:  string;   // ISO-Date oder ''
  geschlecht:    'M' | 'W' | 'D' | 'U';
  aktiv:         boolean;
}

export async function saveMitarbeiter(input: MitarbeiterInput): Promise<void> {
  ensureSession();
  const now = new Date().toISOString();
  const nameJson = JSON.stringify({ vorname: input.vorname.trim(), nachname: input.nachname.trim() });

  const existing = input.id !== undefined ? await db.mitarbeiter.get(toMitarbeiterID(input.id)) : undefined;

  const row: Mitarbeiter = {
    id: toMitarbeiterID(input.id ?? crypto.randomUUID()),
    schema_version: 1,
    mandant_id: toMandantID(input.mandant_id),
    arbeitsplatz_id: toArbeitsplatzID(input.arbeitsplatz_id),
    name_enc: await encField(nameJson),
    geburtsdatum_enc: input.geburtsdatum !== '' ? await encField(input.geburtsdatum) : '',
    geschlecht: input.geschlecht,
    aktiv: input.aktiv,
    erstellt_am: existing?.erstellt_am ?? now,
    geaendert_am: now,
  };
  await db.mitarbeiter.put(row);
  void logAktion(input.mandant_id, existing !== undefined ? 'MITARBEITER_BEARBEITET' : 'MITARBEITER_ERSTELLT');
}

export async function deleteMitarbeiter(id: string): Promise<void> {
  await db.mitarbeiter.delete(toMitarbeiterID(id));
  void logAktion('SYSTEM', 'MITARBEITER_GELOESCHT');
}

export type { MitarbeiterID, MandantID };
